export interface DeviceConfig {
  width: number;
  height: number;
  isMobile: boolean;
  isIOS: boolean;
  hasTouch: boolean;
  hasCoarsePointer: boolean;
  aoEnabled: boolean;
  ssaa: boolean;
}

// Cache for device detection
let cachedConfig: DeviceConfig | null = null;
let lastCheckTime: number = 0;
const CACHE_DURATION = 5000; // Cache for 5 seconds

/**
 * Detect device capabilities
 */
function detectDevice(): DeviceConfig {
  try {
    // Get screen dimensions first (needed for mobile detection)
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Detect mobile capabilities
    const hasCoarsePointer =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    // userAgentData is a newer API not in standard TypeScript types
    const uaDataMobile = !!(navigator as any).userAgentData?.mobile;

    const legacyUaMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    const hasTouch =
      "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;

    // More conservative mobile detection:
    // 1. Trust user agent data first (most reliable)
    // 2. Use screen size as primary indicator (mobile typically < 768px width)
    // 3. Touch/coarse pointer alone don't indicate mobile (many desktops have touch)
    // 4. Only consider touch/coarse pointer if combined with small screen size
    const isMobileByUA = uaDataMobile || legacyUaMobile;
    const isMobileBySize = width < 768; // Typical mobile breakpoint
    const isMobileByTouchAndSize =
      (hasCoarsePointer || hasTouch) && width < 1024;

    const isMobile = isMobileByUA || isMobileBySize || isMobileByTouchAndSize;

    // Detect iOS specifically
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // Performance settings based on device capabilities
    const aoEnabled = !isMobile;
    const ssaa = !isMobile;

    return {
      width,
      height,
      isMobile,
      isIOS,
      hasTouch,
      hasCoarsePointer,
      aoEnabled,
      ssaa
    };
  } catch (error) {
    // Fallback to safe defaults
    return {
      width: window.innerWidth || 1920,
      height: window.innerHeight || 1080,
      isMobile: false,
      isIOS: false,
      hasTouch: false,
      hasCoarsePointer: false,
      aoEnabled: true,
      ssaa: true
    };
  }
}

/**
 * Get device configuration with caching
 */
export function getDeviceConfig(): DeviceConfig {
  const now = Date.now();

  // Return cached config if still valid
  if (cachedConfig && now - lastCheckTime < CACHE_DURATION) {
    return { ...cachedConfig };
  }

  // Detect device configuration
  const config = detectDevice();

  // Cache the result
  cachedConfig = config;
  lastCheckTime = now;

  return { ...config };
}

/**
 * Force refresh device detection (useful for resize events)
 */
export function refreshDeviceConfig(): DeviceConfig {
  cachedConfig = null;
  lastCheckTime = 0;
  return getDeviceConfig();
}

/**
 * Get only mobile detection result
 */
export function isMobile(): boolean {
  return getDeviceConfig().isMobile;
}

/**
 * Get only iOS detection result
 */
export function isIOS(): boolean {
  return getDeviceConfig().isIOS;
}

/**
 * Check if device supports touch
 */
export function hasTouch(): boolean {
  return getDeviceConfig().hasTouch;
}

/**
 * Get screen dimensions
 */
export function getScreenDimensions(): { width: number; height: number } {
  const config = getDeviceConfig();
  return { width: config.width, height: config.height };
}

/**
 * Get performance configuration based on device capabilities
 */
export function getPerformanceConfig(): { aoEnabled: boolean; ssaa: boolean } {
  const config = getDeviceConfig();
  return {
    aoEnabled: !config.isMobile, // Disable AO on mobile for performance
    ssaa: !config.isMobile // Disable SSAA on mobile for performance
  };
}

/**
 * Clear cache (useful for testing or when device capabilities change)
 */
export function clearCache(): void {
  cachedConfig = null;
  lastCheckTime = 0;
}
